import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiGet } from '../../utils/api'
import { fmtDuration } from './ChatHistoryCompaniesView'

interface Att { id: string; url: string; name: string; type: string; size: number }
interface Msg { id: string; sender: string; senderName: string; content: string; messageType: string; createdAt: string; attachments: Att[] }
interface Chat {
  id: string | number
  status: string
  created_at: string
  closed_at: string | null
  visitor_name: string | null
  visitor_email: string | null
  resolution: string | null
  messages: Msg[]
}
interface Detail {
  alias: string
  company_name: string
  company_logo_url?: string | null
  total_chats: number
  total_time_seconds: number
  avg_resolution_seconds: number | null
  chats: Chat[]
}

function fmtDateTime(v: string): string {
  try {
    return new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return v
  }
}
function fmtShort(v: string): string {
  try {
    return new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  } catch {
    return v
  }
}

const MessageRow: React.FC<{ m: Msg; visitor: string | null }> = ({ m, visitor }) => {
  const isAgent = m.sender === 'agent'
  const isSystem = m.sender === 'system' || m.sender === 'bot'
  if (isSystem) {
    return <div className="text-center text-[11px] text-txt-tertiary py-1">{m.content}</div>
  }
  const name = isAgent ? m.senderName || 'Agent' : visitor || 'Customer'
  return (
    <div className={`flex flex-col ${isAgent ? 'items-end' : 'items-start'}`}>
      <div className="text-[10px] text-txt-tertiary mb-0.5 px-1">
        {name} · {fmtShort(m.createdAt)}
      </div>
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
          isAgent ? 'bg-brand-500 text-white' : 'bg-surface border border-border text-txt-primary'
        }`}
      >
        {m.messageType === 'html' ? (
          <div className="prose prose-sm max-w-none prose-invert:false" dangerouslySetInnerHTML={{ __html: m.content }} />
        ) : (
          m.content
        )}
        {m.attachments && m.attachments.length > 0 && (
          <div className="mt-2 flex flex-col gap-2">
            {m.attachments.map((a) =>
              a.type === 'image' ? (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                  <img src={a.url} alt={a.name} className="max-w-[220px] max-h-[220px] rounded-lg border border-black/5" />
                </a>
              ) : (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="text-xs underline break-all">
                  📎 {a.name}
                </a>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export const ChatHistoryCompanyDetail: React.FC = () => {
  const { alias } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<Detail | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    apiGet<Detail>(`/chat-history/companies/${encodeURIComponent(alias || '')}`)
      .then((d) => {
        if (active) {
          setData(d)
          setExpanded(d.chats && d.chats.length ? String(d.chats[0].id) : null)
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [alias])

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/chat-history')}
        className="flex items-center gap-1.5 text-sm text-txt-secondary hover:text-txt-primary mb-4"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to companies
      </button>

      {loading ? (
        <p className="text-sm text-txt-tertiary">Loading…</p>
      ) : !data ? (
        <p className="text-sm text-red-500">Company not found.</p>
      ) : (
        <>
          <div className="mb-5">
            <div className="flex items-center gap-3">
              {data.company_logo_url ? (
                <img
                  src={data.company_logo_url}
                  alt=""
                  className="w-10 h-10 rounded-lg object-contain bg-white border border-border shrink-0"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center font-semibold shrink-0">
                  {(data.company_name || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <h1 className="text-xl font-semibold text-txt-primary">{data.company_name}</h1>
            </div>
            <div className="flex gap-5 mt-2 text-sm text-txt-secondary flex-wrap">
              <span>
                <strong className="text-txt-primary">{data.total_chats}</strong> chats
              </span>
              <span>
                <strong className="text-txt-primary">{fmtDuration(data.total_time_seconds)}</strong> total time
              </span>
              <span>
                <strong className="text-txt-primary">{fmtDuration(data.avg_resolution_seconds)}</strong> avg resolve
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {data.chats.map((chat) => {
              const open = expanded === String(chat.id)
              return (
                <div key={chat.id} className="rounded-xl border border-border bg-surface overflow-hidden">
                  <button
                    onClick={() => setExpanded(open ? null : String(chat.id))}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-hover text-left"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-txt-primary truncate">
                        Chat #{chat.id} · {chat.visitor_name || 'Visitor'}
                        {chat.visitor_email ? <span className="text-txt-tertiary font-normal"> · {chat.visitor_email}</span> : null}
                      </div>
                      <div className="text-[11px] text-txt-tertiary">
                        {fmtDateTime(chat.created_at)} · {chat.status}
                        {chat.resolution ? ` · ${chat.resolution}` : ''} · {chat.messages.length} messages
                      </div>
                    </div>
                    <svg
                      className={`w-4 h-4 text-txt-tertiary transition-transform shrink-0 ml-2 ${open ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {open && (
                    <div className="px-4 py-4 border-t border-border space-y-3 bg-surface-secondary/40">
                      {chat.messages.length === 0 ? (
                        <p className="text-xs text-txt-tertiary">No messages.</p>
                      ) : (
                        chat.messages.map((m) => <MessageRow key={m.id} m={m} visitor={chat.visitor_name} />)
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
